import { SessionContext, signOut } from "next-auth/react";
import { useCallback, useContext, useEffect, useMemo, useRef } from "react";
import axios from "../app/api/axios";
import useErrorHandling from "./useErrorHandling";

const getRealtimeHeaders = () => {
  if (typeof window === "undefined") return {};

  const socketId = window?.Echo?.socketId?.();
  return socketId ? { "X-Socket-Id": socketId } : {};
};

const useApi = () => {
  const [handleError] = useErrorHandling();
  const sessions = useContext(SessionContext);
  const session = sessions?.data || { accessToken: "" };
  const accessTokenRef = useRef(session?.accessToken || "");
  const handleErrorRef = useRef(handleError);

  useEffect(() => {
    accessTokenRef.current = session?.accessToken || "";
  }, [session?.accessToken]);

  useEffect(() => {
    handleErrorRef.current = handleError;
  }, [handleError]);

  const get = useCallback(async (endpoint, showError = true) => {
    let data = null;
    let error = null;
    try {
      const result = await axios.get(endpoint, {
        headers: {
          Authorization: `Bearer ${accessTokenRef.current}`,
          ...getRealtimeHeaders(),
        },
      });
      data = result.data;
    } catch (err) {
      const isCustomerHub = endpoint.includes("/api/customer-hub/");
      const isUnauthorized = err?.response?.status === 401;

      if (isUnauthorized) {
        if (isCustomerHub) {
          await signOut({ redirect: false });
          return { data, error }; // early return to avoid setting error
        }
        await signOut({ callbackUrl: "/login" });
        return { data, error }; // early return to avoid setting error
      }
      error = await handleErrorRef.current(err, showError);
    }
    return { data, error };
  }, []);

  const post = useCallback(async (endpoint, postData, showError = true) => {
    let data = null;
    let error = null;
    try {
      const result = await axios.post(endpoint, postData, {
        headers: {
          Authorization: `Bearer ${accessTokenRef.current}`,
          ...getRealtimeHeaders(),
        },
      });
      data = result.data;
    } catch (err) {
      const isCustomerHub = endpoint.includes("/api/customer-hub/");
      const isUnauthorized = err?.response?.status === 401;

      if (isUnauthorized) {
        if (isCustomerHub) {
          await signOut({ redirect: false });
          return { data, error }; // early return to avoid setting error
        }
        await signOut({ callbackUrl: "/login" });
        return { data, error }; // early return to avoid setting error
      }

      error = await handleErrorRef.current(err, showError);
    }

    return { data, error };
  }, []);

  const put = useCallback(async (endpoint, putData, showError = true) => {
    let data = null;
    let error = null;
    try {
      const result = await axios.put(endpoint, putData, {
        headers: {
          Authorization: `Bearer ${accessTokenRef.current}`,
          ...getRealtimeHeaders(),
        },
      });
      data = result.data;
    } catch (err) {
      const isCustomerHub = endpoint.includes("/api/customer-hub/");
      const isUnauthorized = err?.response?.status === 401;

      if (isUnauthorized) {
        if (isCustomerHub) {
          await signOut({ redirect: false });
          return { data, error }; // early return to avoid setting error
        }
        await signOut({ callbackUrl: "/login" });
        return { data, error }; // early return to avoid setting error
      }
      error = await handleErrorRef.current(err, showError);
    }
    return { data, error };
  }, []);

  const del = useCallback(async (endpoint, body, showError = true) => {
    let data = null;
    let error = null;
    try {
      const result = await axios.delete(endpoint, {
        headers: {
          Authorization: `Bearer ${accessTokenRef.current}`,
          ...getRealtimeHeaders(),
        },
        ...(body ? { data: body } : {}),
      });
      data = result.data;
    } catch (err) {
      const isCustomerHub = endpoint.includes("/api/customer-hub/");
      const isUnauthorized = err?.response?.status === 401;

      if (isUnauthorized) {
        if (isCustomerHub) {
          await signOut({ redirect: false });
          return { data, error }; // early return to avoid setting error
        }
        await signOut({ callbackUrl: "/login" });
        return { data, error }; // early return to avoid setting error
      }
      error = await handleErrorRef.current(err, showError);
    }
    return { data, error };
  }, []);

  return useMemo(() => ({ get, post, put, del }), [get, post, put, del]);
};

export default useApi;
